import { useRouter } from 'next/router';
import React, { useRef, useState, useEffect } from 'react';
import fetch from 'isomorphic-unfetch';
import PropTypes from 'prop-types';
import qs from 'qs';
import { buildDataUrl } from '@helper/api';
import { appSetPageData } from '@redux/actions/app';
import { wrapper } from '@redux/store';
import TemplateDefault from '@components/templates/default/default';
import EducationLibraryTemplate from '@components/organisms/education/library-template/educationLibraryTemplate';
import EducationTopicFilter from '@components/organisms/education/topic-filter/educationTopicFilter';
import EducationLibraryStage from '@components/organisms/education/library-stage/educationLibraryStage';
import EducationNavBg from '@components/atoms/education/nav-bg/educationNavBg';
import { Configure, InstantSearch } from 'react-instantsearch-dom';
import TypesenseInstantsearchAdapter from 'typesense-instantsearch-adapter';

const typesenseInstantSearchAdapter = new TypesenseInstantsearchAdapter({
  server: {
    apiKey: process.env.NEXT_PUBLIC_TYPESENSE_API_KEY,
    nodes: [
      {
        host: process.env.NEXT_PUBLIC_TYPESENSE_HOST,
        port: process.env.NEXT_PUBLIC_TYPESENSE_PORT,
        protocol: process.env.NEXT_PUBLIC_TYPESENSE_PROTOCOL
      }
    ]
  },
  additionalSearchParameters: {
    query_by: 'typeFilter,title,headline,subline,introCopy',
    query_by_weights: '8,6,5,3,1'
  }
});

export const getServerSideProps = wrapper.getServerSideProps(store => async ({ locale, query }) => {
  try {
    const localizedSlug = locale === 'de' ? 'bildung' : 'education';
    const { token } = query;

    const response = await fetch(
      `${buildDataUrl(
        `${locale || 'de'}/${process.env.NEXT_PUBLIC_JSON_API_PATH}/${localizedSlug}/bibliothek${
          token ? `?token=${token}` : ''
        }`
      )}`,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return {
          notFound: true
        };
      }
      throw new Error(`Failed to fetch data: ${response.status}`);
    }

    const data = await response.json();

    await store.dispatch(appSetPageData(data));

    return {
      props: {
        data
      }
    };
  } catch (error) {
    console.error('Error fetching data:', error);
    return {
      props: {
        data: null,
        error: error.message
      }
    };
  }
});

const DEBOUNCE_TIME = 300;

const createURL = state => {
  const isDefaultRoute =
    !state.query &&
    state.page === 1 &&
    state.refinementList &&
    state.refinementList.topicFilter &&
    state.refinementList.topicFilter.length === 0;

  if (isDefaultRoute) {
    return '';
  }

  const queryParameters = {};

  if (state.query) {
    queryParameters.query = encodeURIComponent(state.query);
  }
  if (state.page !== 1) {
    queryParameters.page = state.page;
  }
  if (state.refinementList && state.refinementList.topicFilter) {
    queryParameters.topicFilter = state.refinementList.topicFilter.map(encodeURIComponent);
  }

  const queryString = qs.stringify(queryParameters, {
    arrayFormat: 'repeat'
  });

  return `${queryString}`;
};

const searchStateToUrl = searchState => (searchState ? createURL(searchState) : '');

const urlToSearchState = searchParams => {
  const { query = '', page = 1, topicFilter = [] } = qs.parse(searchParams);
  // `qs` does not return an array when there's a single value.
  const allTopicFilter = Array.isArray(topicFilter) ? topicFilter : [topicFilter].filter(Boolean);

  return {
    query: decodeURIComponent(query),
    page,
    refinementList: {
      topicFilter: allTopicFilter ? allTopicFilter.map(decodeURIComponent) : []
    }
  };
};

function Bibiliothek({ data }) {
  const router = useRouter();
  const indexName = data.lang === 'en' ? 'futurium_education_en' : 'futurium_education_de';
  const [searchState, setSearchState] = useState(urlToSearchState(router.query));
  const debouncedSetStateRef = useRef(null);

  const onSearchStateChange = updatedSearchState => {
    clearTimeout(debouncedSetStateRef.current);

    debouncedSetStateRef.current = setTimeout(() => {
      router.push(
        {
          pathname: router.pathname,
          query: searchStateToUrl(updatedSearchState)
        },
        undefined,
        { shallow: true }
      );
    }, DEBOUNCE_TIME);

    setSearchState(updatedSearchState);
  };

  useEffect(() => {
    setSearchState(urlToSearchState(router.query));
  }, []); // <- Use empty array here

  return (
    <TemplateDefault invertedHeader>
      <EducationNavBg overlap />
      <InstantSearch
        searchClient={typesenseInstantSearchAdapter.searchClient}
        searchState={searchState}
        onSearchStateChange={onSearchStateChange}
        createURL={createURL}
        indexName={indexName}
      >
        <Configure hitsPerPage={14} />
        <EducationLibraryStage />
        <EducationTopicFilter />
        <EducationLibraryTemplate />
      </InstantSearch>
    </TemplateDefault>
  );
}

Bibiliothek.propTypes = {
  data: PropTypes.shape({
    lang: PropTypes.string
  })
};

export default Bibiliothek;
